import router from '@/router'
import store from '@/store'
import { SET_NAV_TO_HINT } from '@/store/mutations'
import isAbsoluteURL from '@/utils/isAbsoluteURL'

const boundValues = new WeakMap()

function formatRouteHint(value) {
  if (isAbsoluteURL(value)) {
    return ''
  }
  value = computeFinalRoute(value)
  const route = router.matcher.match(value)
  let bucketName = ''
  if (route.params.id !== store.state.currentBucketId) {
    const bucketState = store.state.buckets[route.params.id]
    if (bucketState) {
      bucketName = `bucket ${bucketState.bucket.name}`
    }
  }
  const isApp = route.meta.requireApp
  const appName = isApp ? route.query.app : ''

  return `Go to ${bucketName} ${appName} ${route.meta.title.toLowerCase()}`
}

function computeFinalRoute(value) {
  const partialRoute = typeof value === 'string' ? { name: value } : value
  const currentRoute = router.currentRoute
  return {
    name: partialRoute.name || currentRoute.name,
    params: { ...currentRoute.params, ...value.params },
    query: { ...currentRoute.query, ...value.query },
  }
}

let activeElement
let setHintTimeout

function activateElement(element, value) {
  if (activeElement && activeElement !== element) {
    deactivateElement(activeElement)
  }
  activeElement = element
  clearTimeout(setHintTimeout)
  setHintTimeout = setTimeout(() => {
    store.commit(SET_NAV_TO_HINT, { hint: formatRouteHint(value) })
  }, 100)
}

function deactivateElement(element) {
  if (element === activeElement) {
    activeElement = null
    clearTimeout(setHintTimeout)
    store.commit(SET_NAV_TO_HINT, { hint: '' })
  }
}

function navigate(value) {
  if (isAbsoluteURL(value)) {
    window.open(value, '_blank')
  } else {
    router.push(computeFinalRoute(value))
  }
}

function listener(event) {
  const value = boundValues.get(event.currentTarget)
  if (!value) return
  if (event.target.tagName === 'A' && event.target.getAttribute('href')) return
  if (event.type === 'click') {
    event.preventDefault()
    event.stopPropagation()
    navigate(value)
  } else if (event.type === 'mouseenter') {
    activateElement(event.currentTarget, value)
  } else if (event.type === 'mouseleave') {
    deactivateElement(event.currentTarget)
  }
}

function bind(el, value) {
  boundValues.set(el, value)
  el.classList.add('nav-to-element')
  el.addEventListener('click', listener)
  el.addEventListener('mouseenter', listener)
  el.addEventListener('mouseleave', listener)
}

function unbind(el) {
  deactivateElement(el)
  el.classList.remove('nav-to-element')
  el.removeEventListener('click', listener)
  el.removeEventListener('mouseenter', listener)
  el.removeEventListener('mouseleave', listener)
}

export default {
  name: 'nav-to',
  bind(el, binding) {
    if (binding.value) bind(el, binding.value)
  },
  update(el, binding) {
    if (!binding.oldValue) {
      if (binding.value) bind(el, binding.value)
    } else {
      if (!binding.value) unbind(el)
      else {
        el.classList.add('nav-to-element')
        boundValues.set(el, binding.value)
        if (el === activeElement) activateElement(el, binding.value)
      }
    }
  },
  unbind(el) {
    unbind(el)
  },
}

export { activateElement, deactivateElement, navigate }
